// frontend/lib/hooks/useFacilityCompliance.ts
"use client";

import { useCallback, useEffect, useState } from "react";
import { apiGet } from "../apiClient";

// =============================================================================
// Types (matching backend response shapes)
// =============================================================================

export interface FacilityComplianceIssue {
  credential_type_id: number;
  credential_type_name: string;
  reason: string;
}

export interface CredentialIssueCount {
  credential_type_id: number;
  credential_type_name: string;
  count: number;
}

export interface StateIssueCount {
  state: string;
  count: number;
}

export interface TopIssues {
  missing_credentials: CredentialIssueCount[];
  expired_credentials: CredentialIssueCount[];
  not_verified: CredentialIssueCount[];
  state_mismatch: StateIssueCount[];
}

export interface ContractCounts {
  total_active: number;
  compliant: number;
  at_risk: number;
  non_compliant: number;
}

export interface ApplicationCounts {
  total_pipeline: number;
  compliant: number;
  non_compliant: number;
}

export interface FacilityComplianceSummary {
  facility_id: string;
  generated_at: string;
  contracts: ContractCounts;
  applications: ApplicationCounts;
  top_issues: TopIssues;
}

export interface ApplicationComplianceItem {
  application_id: string;
  job_id: string;
  contract_id: string | null;
  nurse_id: string;
  nurse_name: string;
  nurse_email: string;
  job_title: string;
  job_specialty: string;
  job_state: string;
  job_start_date: string;
  application_status: string;
  compliance_status: "COMPLIANT" | "AT_RISK" | "NON_COMPLIANT";
  issues: FacilityComplianceIssue[];
  last_checked_at: string;
}

export interface ContractComplianceItem {
  contract_id: string;
  application_id: string;
  job_id: string;
  nurse_id: string;
  nurse_name: string;
  nurse_email: string;
  job_title: string;
  job_specialty: string;
  job_state: string;
  contract_start_date: string;
  contract_end_date: string;
  contract_status: string;
  compliance_status: "COMPLIANT" | "AT_RISK" | "NON_COMPLIANT";
  issues: FacilityComplianceIssue[];
  last_checked_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
}

export type ComplianceFilter = "ALL" | "COMPLIANT" | "AT_RISK" | "NON_COMPLIANT";

// =============================================================================
// Summary Hook
// =============================================================================

export function useFacilityComplianceSummary() {
  const [data, setData] = useState<FacilityComplianceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiGet<FacilityComplianceSummary>(
        "/facilities/me/compliance/summary"
      );
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// =============================================================================
// Applications Hook
// =============================================================================

interface UseApplicationsParams {
  page?: number;
  pageSize?: number;
  complianceFilter?: ComplianceFilter;
  statusScope?: "PIPELINE" | "ALL";
}

export function useFacilityComplianceApplications(
  params: UseApplicationsParams = {}
) {
  const {
    page = 1,
    pageSize = 50,
    complianceFilter = "ALL",
    statusScope = "PIPELINE",
  } = params;

  const [data, setData] = useState<PaginatedResponse<ApplicationComplianceItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("page", String(page));
      searchParams.set("page_size", String(pageSize));
      searchParams.set("status_scope", statusScope);
      searchParams.set("compliance_filter", complianceFilter);

      const response = await apiGet<PaginatedResponse<ApplicationComplianceItem>>(
        `/facilities/me/compliance/applications?${searchParams.toString()}`
      );
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, complianceFilter, statusScope]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

// =============================================================================
// Contracts Hook
// =============================================================================

interface UseContractsParams {
  page?: number;
  pageSize?: number;
  complianceFilter?: ComplianceFilter;
  statusScope?: "ACTIVE" | "ALL";
}

export function useFacilityComplianceContracts(
  params: UseContractsParams = {}
) {
  const {
    page = 1,
    pageSize = 50,
    complianceFilter = "ALL",
    statusScope = "ACTIVE",
  } = params;

  const [data, setData] = useState<PaginatedResponse<ContractComplianceItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("page", String(page));
      searchParams.set("page_size", String(pageSize));
      searchParams.set("status_scope", statusScope);
      searchParams.set("compliance_filter", complianceFilter);

      const response = await apiGet<PaginatedResponse<ContractComplianceItem>>(
        `/facilities/me/compliance/contracts?${searchParams.toString()}`
      );
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, complianceFilter, statusScope]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}
